import {
    NavbarDropdown,
    NavbarDropdownBody,
    NavbarDropdownHeader,
    NavbarDropdownItem,
    NavbarDropdownVariant,
} from './NavbarDropdown.tsx';
import { CurrentOperatorLoginText } from './CurrentOperatorLogin.tsx';
import * as AuthenticationActions from '../../app/actions/AuthenticationActions.ts';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import { ALLOWED_LANGUAGE_CODES } from '../../common/i18n/Text.ts';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { css } from '@emotion/css';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import { type JSX, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router';
import { useReadOnlyExportContext } from '../../app/components/ReadOnlyExportContext.tsx';
import { languageFromString } from 'dg-web-shared/lib/Localized.ts';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { TwoFactorAuthentificationIcon } from '../../operator-logins/components/OperatorLoginsList.tsx';

export function CurrentOperatorLoginDropdown() {
    const update = useUpdate();
    const { language, setLanguage } = useLanguage();
    const navigate = useNavigate();

    const { newDownloads } = useReadOnlyExportContext();
    const {
        operatorData: { operatorExternalId },
        currentLogin: { otpEnabled },
    } = useOperatorContext();

    const [show, setShow] = useState(false);

    return (
        <NavbarDropdown onClose={() => setShow(false)}>
            <NavbarDropdownHeader
                variant={NavbarDropdownVariant.Right}
                open={show}
                onClick={() => setShow(!show)}
                hasNewDownloads={newDownloads > 0}
            >
                <CurrentOperatorLoginText />
            </NavbarDropdownHeader>
            <NavbarDropdownBody
                open={show}
                variant={NavbarDropdownVariant.Right}
            >
                <NavbarDropdownItem
                    label={<ProfileLabel otpEnabled={otpEnabled} />}
                    onClick={() => navigate(`/${operatorExternalId}/profile`)}
                    selected={false}
                />
                <NavbarDropdownItem
                    label={
                        <Localized
                            de="Passwort"
                            fr="Mot de passe"
                            it="Password"
                            en="Password"
                        />
                    }
                    onClick={() =>
                        navigate(`/${operatorExternalId}/profile/password`)
                    }
                    selected={false}
                />
                <LanguageSelector
                    language={language}
                    onChange={language =>
                        setLanguage(languageFromString(language))
                    }
                />
                <NavbarDropdownItem
                    label={<DownloadsLabel newDownloads={newDownloads} />}
                    onClick={() => navigate('downloads')}
                    selected={false}
                />
                <NavbarDropdownItem
                    label="Logout"
                    onClick={() => update(AuthenticationActions.logout)}
                    selected={false}
                />
            </NavbarDropdownBody>
        </NavbarDropdown>
    );
}

function ProfileLabel({ otpEnabled }: { otpEnabled: boolean }) {
    return (
        <Stack
            direction="row"
            sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Localized de="Profil" fr="Profil" it="Profilo" en="Profile" />
            {otpEnabled && <TwoFactorAuthentificationIcon />}
        </Stack>
    );
}

function DownloadsLabel({ newDownloads }: { newDownloads: number }) {
    return (
        <Stack
            direction="row"
            sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Localized
                de="Downloads"
                fr="Téléchargements"
                it="Downloads"
                en="Downloads"
            />
            {newDownloads > 0 && (
                <Box
                    sx={{
                        backgroundColor: theme => theme.palette.warning.main,
                        width: '24px',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {newDownloads}
                </Box>
            )}
        </Stack>
    );
}

interface LanguageSelectorProps {
    language: string;
    onChange: (language: string) => void;
}

function LanguageSelector(props: LanguageSelectorProps): JSX.Element {
    return (
        <div
            className={css({
                padding: '12px 14px',
            })}
        >
            <span className={css({ marginRight: 5 })}>
                <Localized de="Sprache" fr="Langue" it="Lingua" en="Language" />
                :
            </span>
            {ALLOWED_LANGUAGE_CODES.map(lng => (
                <Clickable
                    key={lng}
                    element="span"
                    disabled={props.language === lng}
                    onClick={() => props.onChange(lng)}
                    className={css({
                        padding: '0 7px',
                        color: props.language === lng ? '#2E91DF' : undefined,
                    })}
                >
                    {lng}
                </Clickable>
            ))}
        </div>
    );
}
